/**
 * Fetches a specified number of random dive updates from the API.
 * The limit is set to 4 and the days to look back are set to 30 directly in the API endpoint.
 *
 * @returns {Promise<Array>} - A Promise that resolves with an array of dive update objects.
 * @throws {Error} - Throws an error if the fetch fails.
 */
const getRandomDiveUpdates = async () => {
  const limit = 4; // Define the limit here looks better when even
  const days = 30;   // Define the days here
  const API_ENDPOINT = `/api/dives/random-updated?limit=${limit}&days=${days}`;

  try {
    const response = await fetch(API_ENDPOINT);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error fetching dive updates: ${response.status} - ${errorText}`);
      throw new Error(`Failed to fetch dive updates: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error in getRandomDiveUpdates:', error);
    throw error;
  }
};
/**
 * Processes the dive updates for display on the homepage.
 * Filters out dives associated with unpublished divesites and orders them chronologically (most recent first).
 * Includes the common names of seen creatures.
 *
 * @param {Array} diveUpdates - An array of dive update objects (from the API).
 * @returns {Array|null} - An array of processed dive updates, or null if no valid dives.
 */
const processDiveUpdatesForDisplay = (diveUpdates) => {
  if (!Array.isArray(diveUpdates)) {
    console.error("Invalid input: diveUpdates must be an array.", diveUpdates);
    return null;
  }

  try {
    const processedUpdates = diveUpdates
      .filter(dive => dive.divesite?.lifecycle?.published === true) // Filter dives
      .map(dive => {
        const formattedDate = new Date(dive.updatedAt).toLocaleDateString(
          undefined,
          {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }
        );
        // Extract city information
        const city = dive.divesite?.uddf?.divesite?.site?.geography?.address?.city;
        const country = dive.divesite?.uddf?.divesite?.site?.geography?.address?.country;
        
        // Create location string with city and country if available
        let divesiteLocation = 'Unknown Location';
        if (city && country) {
          divesiteLocation = `${city}, ${country}`;
        } else if (city) {
          divesiteLocation = city;
        } else if (country) {
          divesiteLocation = country;
        }
        return {
          diveId: dive._id,
          divesiteName: dive.divesite?.uddf?.divesite?.site?.name || 'Unknown Divesite',
          divesiteLocation: divesiteLocation || 'Unknown Location',
          updatedAt: formattedDate,
          comments: dive.comments || 'No comments provided.',
          diverName: dive.diver?.displayName || dive.diver?.email || 'Anonymous Diver',
          visibilityRating: dive.visibilityRating,
          seenCreatures: dive.seenItems ? dive.seenItems.map(creature => creature.commonName) : [], // Extract common names
          originalUpdatedAt: new Date(dive.updatedAt), // Keep the original Date object for sorting
        };
      })
      .sort((b, a) => a.originalUpdatedAt.getTime() - b.originalUpdatedAt.getTime()); // Sort by date, newest first

    if (processedUpdates.length === 0) {
      return null; // Return null if no valid dives remain after filtering
    }
    return processedUpdates;
  } catch (error) {
    console.error('Error in processDiveUpdatesForDisplay:', error);
    return null;
  }
};

/**
 * Gets random dive updates for the homepage, handling API calls and data processing.
 *
 * @returns {Promise<Array|null>} - A Promise that resolves with an array of processed dive updates, or null on error.
 */
export const getRandomUpdatesForHomePage = async () => {
  try {
    const rawUpdates = await getRandomDiveUpdates(); // Call without parameters
    const processedUpdates = processDiveUpdatesForDisplay(rawUpdates);
    return processedUpdates;
  } catch (error) {
    // Handle errors from getRandomDiveUpdates
    console.error("Error in getRandomUpdatesForHomePage:", error);
    return null;
  }
};